import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Target, CheckCircle, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function StressResponseGuide() {
  const navigate = useNavigate();

  const ifThenExamples = [
    { trigger: "Boss sends stressful email", response: "Take 5 deep breaths before responding" },
    { trigger: "Traffic jam", response: "Play calming music and practice gratitude" },
    { trigger: "Family conflict starts", response: "Say 'Let's pause and discuss this calmly'" },
    { trigger: "Feel overwhelmed by to-do list", response: "Pick just 3 priorities and ignore the rest" },
    { trigger: "Someone criticizes me", response: "Count to 10, then ask clarifying questions" },
    { trigger: "Can't fall asleep due to worry", response: "Write worries in journal, then do 4-7-8 breathing" },
    { trigger: "Running late", response: "Accept it, text ahead, and let go of rushing" },
    { trigger: "Unexpected expense", response: "Check savings, make a plan, don't catastrophize" }
  ];

  const stressResponseCycle = [
    { step: "Notice", desc: "Recognize you're stressed (tight shoulders, racing heart, irritability)" },
    { step: "Pause", desc: "Stop before reacting—take one deep breath" },
    { step: "Choose", desc: "Ask: 'What's my if-then plan for this?'" },
    { step: "Respond", desc: "Execute your planned response instead of reacting" },
    { step: "Reflect", desc: "After the situation, notice what worked" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold mb-4">
            Week 6 • Day 4 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Stress Response Planning
          </h1>
          <p className="text-xl text-secondary-600">Prepare Your Response Before Stress Hits</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-violet-500 to-purple-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Shield className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Exercise</h3>
              <p className="text-violet-100 text-lg">
                For each of your top 3 stressors, create an 'If-Then' plan. When you have strategies ready, 
                you feel more in control and less overwhelmed when stress hits.
              </p>
            </div>
          </div>
        </section>

        {/* The Cycle */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">The Mindful Response Cycle</h2>
          <div className="flex flex-col md:flex-row gap-4">
            {stressResponseCycle.map((item, index) => (
              <div key={index} className="flex-1 bg-violet-50 rounded-xl p-4 text-center">
                <div className="w-10 h-10 bg-violet-500 text-white rounded-full flex items-center justify-center font-bold mx-auto mb-2">
                  {index + 1}
                </div>
                <h3 className="font-bold text-violet-700 mb-1">{item.step}</h3>
                <p className="text-sm text-secondary-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* If-Then Examples */}
        <section className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-8 border-2 border-violet-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Target className="w-6 h-6 text-violet-500" />
            If-Then Plan Examples
          </h2>
          <div className="space-y-3">
            {ifThenExamples.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                <span className="text-secondary-700"><strong>If:</strong> {item.trigger}</span>
                <span className="text-2xl hidden md:block">→</span>
                <span className="text-violet-700"><strong>Then:</strong> {item.response}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Create Your Own */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Create Your If-Then Plans</h2>
          <div className="space-y-4">
            {[1, 2, 3].map(n => (
              <div key={n} className="bg-violet-50 rounded-xl p-4">
                <p className="font-medium text-violet-700 mb-2">Stressor #{n}</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-secondary-500 mb-1">If (trigger):</p>
                    <div className="h-10 border-b-2 border-violet-200"></div>
                  </div>
                  <div>
                    <p className="text-sm text-secondary-500 mb-1">Then (my response):</p>
                    <div className="h-10 border-b-2 border-violet-200"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default StressResponseGuide;
